import React, { useState } from 'react';

import { styled } from '@mui/material';
import { Theme } from '@mui/material/styles';

import ChevronLeftIcon from '~/assets/icons/ChevronLeftIcon';
import ChevronRightIcon from '~/assets/icons/ChevronRightIcon';
import { SMALL_MARGIN } from '~/theme';

const DEFAULT_CURRENT_ITEM = 1;
const DEFAULT_STEP = 10;

export const usePagination = (step = DEFAULT_STEP) => {
  const [totalItems, setTotalItems] = useState(DEFAULT_CURRENT_ITEM);
  const [currentItem, setCurrentItem] = useState(DEFAULT_CURRENT_ITEM);
  const currentPage = Math.ceil(currentItem / step);

  const handleNext = () =>
    setCurrentItem((old) => (old < totalItems - step ? old + step : totalItems));
  const handlePrev = () =>
    setCurrentItem((old) =>
      old - step <= DEFAULT_CURRENT_ITEM ? DEFAULT_CURRENT_ITEM : old - step,
    );

  return {
    currentItem,
    currentPage,
    handlePrev,
    handleNext,
    setTotalItems,
  };
};

interface PaginationProps {
  total: number;
  current: number;
  step: number;
  handleNext: () => void;
  handlePrev: () => void;
}

const Pagination = ({
  total = DEFAULT_CURRENT_ITEM,
  current = DEFAULT_CURRENT_ITEM,
  step = DEFAULT_STEP,
  handleNext,
  handlePrev,
  ...rest
}: PaginationProps) => {
  return (
    <Wrapper {...rest}>
      {current}-{current + step <= total ? current + step - 1 : total} from {total}
      <div>
        <ArrowLeft active={current > DEFAULT_CURRENT_ITEM} onClick={handlePrev} />
        <ArrowRight active={current <= total - step} onClick={handleNext} />
      </div>
    </Wrapper>
  );
};

export default Pagination;

const Wrapper = styled('div')(({ theme }: { theme: Theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  fontSize: 16,
  color: theme.palette.common.gray,
}));
const ArrowLeft = styled(ChevronLeftIcon, {
  shouldForwardProp: (prop) => prop !== 'active',
})`
  width: 12px;
  padding: 6px;
  margin-left: ${SMALL_MARGIN}px;
  box-sizing: content-box;
  color: ${({ active, theme }) =>
    active ? theme.palette.common.primary : theme.palette.common.white};
  cursor: ${({ active }) => (active ? 'pointer' : 'initial')};
  pointer-events: ${({ active }) => (!active ? 'none' : 'initial')};
`;
const ArrowRight = styled(ChevronRightIcon, {
  shouldForwardProp: (prop) => prop !== 'active',
})`
  width: 12px;
  padding: 6px;
  margin-left: 10px;
  box-sizing: content-box;
  color: ${({ active, theme }) =>
    active ? theme.palette.common.primary : theme.palette.common.white};
  cursor: ${({ active }) => (active ? 'pointer' : 'initial')};
  pointer-events: ${({ active }) => (!active ? 'none' : 'initial')};
`;
